import React, { HTMLProps, useCallback } from 'react';
import gql from 'graphql-tag';

import { InputDlg } from './input-dialog';
import { Form as FForm, Formik, useField, useFormikContext } from 'formik';
import {
  ButtonSearch,
  FieldInput,
  FormCol6,
  FilterProps,
  FormRow,
  TableCellV8,
  FieldLabel,
  SelectColumnV8,
  promiseModalMultiSelect,
} from 'motion-components';
import { Form, Col } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
// import { SelectColumnV8 } from 'table-v8/cellsV8';
// import { promiseModalSelect } from './modal-select';

interface Props {
  initValueId: string;
  initValueName: string;
  onValueChange: (valueId: string, valueName: string) => void;
  required?: boolean;
  initFilters?: any;
}

interface FFProps extends Props {
  label?: string;
  name?: string;
  displayName?: string;
  as?: React.ElementType<any>;
  disable?: boolean;
}

/* --------------------------------------------------------
   Select Region with Modal Search and Label Formik Form
   -------------------------------------------------------- */
export const FormFieldMultiRegionDlg = ({
  label = 'Περιφέρειες',
  name = 'regionId',
  displayName = 'regionName',
  as = 'div',
  initValueId = undefined,
  initValueName = '',
  onValueChange,
  required = false,
  initFilters = {},
  disable = false,
}: FFProps) => {
  const [{ value }, { touched, error, initialValue }] = useField<number>(name);
  const { setFieldValue, setFieldTouched } = useFormikContext<any>();

  const setFieldValueAndTouched = useCallback(
    async (fieldName, _value, touched: boolean) => {
      //await Promise.all([setFieldValue(name, _value, true), setFieldTouched(name, touched)]);
      await setFieldValue(fieldName, _value, true); //field,value,shouldValidate
      await setFieldTouched(fieldName, touched); //field,touched,shouldValidate
    },
    [name, setFieldTouched, setFieldValue],
  );

  const handleChange = useCallback(
    (valueId: string, valueName: string) => {
      setFieldValueAndTouched(name, valueId, true);
      setFieldValueAndTouched(displayName, valueName, true);
      if (onValueChange) onValueChange(valueId, valueName);
    },
    [setFieldValueAndTouched],
  );

  const changed = value !== initialValue;

  return (
    <Form.Group as={as} controlId={name} className="mb-2">
      <FieldLabel text={label} changed={changed} required={required} />
      <div>
        <FieldMultiRegionDlg
          initValueId={initValueId}
          initValueName={initValueName}
          onValueChange={handleChange}
          initFilters={initFilters}
          disable={disable}
        />
        {touched && !!error && (
          <div className="text-danger select-feedback">
            <small>{error}</small>
          </div>
        )}
      </div>
    </Form.Group>
  );
};

/* ----------------------------------
   Select Region with Modal Search
   ---------------------------------- */

export const FieldMultiRegionDlg = ({
  initValueId = undefined,
  initValueName = '',
  onValueChange,
  required = false,
  initFilters = {},
  disable = false,
}: Props & { disable?: boolean }) => {
  const { t } = useTranslation();
  const { appStore } = useRootStore();
  const valueChanged = (valueId, valueName) => {
    onValueChange(valueId, valueName);
  };

  return (
    <InputDlg
      id={'regionIds'}
      valueId={initValueId}
      valueName={initValueName}
      onDlgClick={async (event) => {
        let res = await promiseModalSelectMultiMaterial({
          initFilters,
          appStore,
          idValues: event.valueId,
          nameValues: event.valueName,
          title: t('Επιλογή Περιφερειών'),
        });
        if (res === false) return; // Cancel Pressed

        res = res.sort((a, b) => {
          const nameA = a.name.toUpperCase(); // ignore upper and lowercase
          const nameB = b.name.toUpperCase(); // ignore upper and lowercase
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          // names must be equal
          return 0;
        });

        const allIds = res.map((el) => el.id).join(',');
        const allNames = res.map((el) => el.name).join(', ');
        return { newId: allIds, newName: allNames };
      }}
      onValueChange={valueChanged}
      disable={disable}
      readOnly={true}
    />
  );
};

// -------------------------------------------------------------------------------
// Modal Select Material
// -------------------------------------------------------------------------------

const gqlGet = gql`
  query getRegions($where: String, $offset: Int, $limit: Int, $order: String) {
    getRegions(where: $where, offset: $offset, limit: $limit, order: $order) {
      data {
        id
        name
      }
      recordCount
    }
  }
`;

const s = typeof SelectColumnV8 === 'function' ? SelectColumnV8() : SelectColumnV8;
const columns = [
  s,
  {
    id: 'name',
    header: 'Περιφέρεια',
    columnName: 'Περιφέρεια',
    accessorKey: 'name',
    orderByField: 'name',
    cell: TableCellV8,
    size: 120,
  },
];

// -------------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------------
const buildWhereFromFilters = (values) => {
  // Φτιάξε το where για το graphql
  let where = {};
  if (values) {
    if (values.searchText)
      where = {
        ...where,
        ...{
          name: { $like: '%' + values.searchText + '%' },
        },
      };
  }
  return where;
};

const Filters: (props: FilterProps) => JSX.Element = ({ onChange, initialValues }) => {
  const { t } = useTranslation();
  return (
    <Formik initialValues={initialValues ? initialValues : { searchText: '' }} onSubmit={onChange}>
      <Form as={FForm}>
        <FormRow className="mb-3 align-items-end">
          <FieldInput
            as={FormCol6}
            name="searchText"
            id="searchText"
            label={t('Περιφέρεια')}
            type="search"
          />
          <Col className="ps-0 pe-0 me-3 ">
            <ButtonSearch size="sm" type="submit" className="float-end mb-2" />
          </Col>
        </FormRow>
      </Form>
    </Formik>
  );
};

// -------------------------------------------------------------------------------

export const promiseModalSelectMultiMaterial = (props) => {
  return promiseModalMultiSelect({
    ...props,
    title: 'Επιλογή Περιφερειών',
    columns,
    gqlQuery: gqlGet,
    buildWhereFromFilters: buildWhereFromFilters,
    initFilters: props.initFilters,
    Filters: Filters,
    getRowId: (row) => row.id,
    idFieldName: 'id',
    nameFieldName: 'name',
    autoFetchData: true,
  });
};
// -------------------------------------------------------------------------------

// export function SelectColumnV8(headerTitle?: string) {
//   return {
//     id: 'select-col',
//     // header: headerTitle
//     //   ? headerTitle
//     //   : ({ table }) => (
//     //       <div className="text-center">
//     //         <input
//     //           type="checkbox"
//     //           checked={table.getIsAllRowsSelected()}
//     //           // indeterminate={table.getIsSomeRowsSelected()}
//     //           onChange={table.getToggleAllRowsSelectedHandler()} //or getToggleAllPageRowsSelectedHandler
//     //         />
//     //       </div>
//     //     ),
//     header: ({ table }) => (
//       <IndeterminateCheckbox
//         {...{
//           checked: table.getIsAllRowsSelected(),
//           indeterminate: table.getIsSomeRowsSelected(),
//           onChange: table.getToggleAllRowsSelectedHandler(),
//         }}
//       />
//     ),
//     cell: ({ row }) => (
//       <div className="text-center">
//         <input
//           type="checkbox"
//           checked={row.getIsSelected()}
//           disabled={!row.getCanSelect()}
//           onChange={row.getToggleSelectedHandler()}
//         />
//       </div>
//     ),
//     size: 50,
//   };
// }

// function IndeterminateCheckbox({
//   indeterminate,
//   className = '',
//   ...rest
// }: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
//   const ref = React.useRef<HTMLInputElement>(null!);

//   React.useEffect(() => {
//     if (typeof indeterminate === 'boolean') {
//       ref.current.indeterminate = !rest.checked && indeterminate;
//     }
//   }, [ref, indeterminate]);

//   return <input type="checkbox" ref={ref} className={className + ' cursor-pointer'} {...rest} />;
// }
